import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    CircularProgress,
    Box,
    Typography,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { IDetalheProdutoEstoque } from "../../../../../../../types/api/estoque/detalhar-produto-estoque";
import axiosClient from "../../../../../../../lib/axios";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { EditarInformacoesBasicasPayload } from "../../../../../../../types/mutations/estoque/editar-produtos";
import { validateEAN13 } from "../../../../../../../helpers/validateEAN13";
import SelectDepartamentoEstoqueMultiple
    from "../../../../../../../components/SelectsSimpleApiQuery/SelectDepartamentoEstoque/Multiple";
import SelectSetor from "../../../../../../../components/SelectsSimpleApiQuery/SelectSetor";
import { useMemo, useState, useEffect } from "react";
import SelectCategoriaProduto from "../../../../../../../components/SelectsSimpleApiQuery/SelectCategoriaProduto";
import { Atributo } from "../../../DialogCadastrarProduto/Step01";
import { formatCurrencyCategory } from "../../../../../../../helpers/masks";
import { CategoriaHierarquica } from "../AdicionarCategoriaModal";

type Props = {
    open: boolean;
    onClose: () => void;
    produto: IDetalheProdutoEstoque;
};

export default function DialogEditInformacoesBasicas({ open, onClose, produto }: Props) {
    const [selectedCategory, setSelectedCategory] = useState<CategoriaHierarquica | null>(
        {
            id: produto.categorias?.[0]?.id || 0,
            nome: produto.categorias?.[0]?.nome_categoria || "",
            atributos: produto.categorias?.[0]?.categoria_atributos || [],
            ordem: [],
            subcategorias: [],
        }
    );
    const [categoriaId, setCategoriaId] = useState<number | null>(produto.categorias?.[0]?.id || null);
    const [caminho, setCaminho] = useState<string>(produto.categorias?.[0]?.nome_categoria ?? "");

    const { control, handleSubmit, formState: { errors }, setValue } = useForm<EditarInformacoesBasicasPayload>({
        defaultValues: {
            nome: produto.nome ?? "",
            codigo_de_barra: produto.codigo_de_barra ?? "",
            descricao_complementar: produto.descricao_complementar ?? "",
            atributos: produto.categorias?.[0]?.atributos || {},
            departamento: produto.departamento ?? null,
            setor: produto.setor ?? null,
            categorias: produto.categorias?.map(cat => cat.id) ?? [],
        }
    });

    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const { mutate: saveProduto, isLoading: isSavingProduto } = useMutation({
        mutationKey: ['editar-produto', produto.id],
        mutationFn: (payload: EditarInformacoesBasicasPayload) =>
            axiosClient.put(`/estoque/produtos/editar-produto/${produto.id}`, {
                ...payload,
                departamento: payload.departamento?.id,
                setor: payload.setor?.id,
                ...(payload.atributos && Object.keys(payload.atributos).length > 0 && { atributos: payload.atributos })
            }),
        onSuccess: () => {
            enqueueSnackbar("Produto alterado com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("detalhe-produto");
            onClose();
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || "Erro ao editar produto!", { variant: "error" });
        }
    });

    const { mutate: atualizarCategoria, isLoading: isAtualizandoCategoria } = useMutation({
        mutationFn: (dadosCategoria: { categoria_id: number, atributos?: Record<string, string>, nome: string }) =>
            axiosClient.post('/estoque/produtos/adicionar-categoria-produto', {
                produto_id: produto.id,
                nome_categoria: dadosCategoria.nome,
                categoria_id: dadosCategoria.categoria_id || produto.categorias?.[0]?.id,
                atributos: dadosCategoria.atributos || {}
            }),
        onSuccess: () => {
            enqueueSnackbar("Categoria atualizada com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("detalhe-produto");
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || "Erro ao atualizar categoria!", { variant: "error" });
        }
    });

    const atributos = useWatch({
        control,
        name: "atributos"
    });

    const nomeGeradoAtributos = useMemo(() => {
        if (!selectedCategory?.atributos || !atributos) return '';

        const atributosQueCompoem = selectedCategory.atributos
            .filter((attr: Atributo) => attr.compoe_nome)
            .sort((a, b) => (a.ordem_nome || 0) - (b.ordem_nome || 0));

        const partes = atributosQueCompoem
            .map(attr => {
                const valor = atributos[attr.nome];
                return valor && String(valor).trim() ? String(valor).trim() : null;
            })
            .filter(Boolean);

        return partes.join(' ');
    }, [atributos, selectedCategory]);

    useEffect(() => {
        if (nomeGeradoAtributos) {
            setValue('nome', nomeGeradoAtributos, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [nomeGeradoAtributos, setValue]);

    const onSubmit = (values: EditarInformacoesBasicasPayload) => {
        const { atributos = {}, ...restValues } = values;

        saveProduto({
            ...restValues,
            ...(Object.keys(atributos).length > 0 && { atributos })
        });

        if (selectedCategory && categoriaId) {
            atualizarCategoria({
                categoria_id: categoriaId,
                nome: caminho,
                atributos: values.atributos || {}
            });
        }
    };

    const handleCategoriaChange = (
        id: number | null,
        caminhoFormatado?: string,
        categoria?: CategoriaHierarquica
    ) => {
        if (id && categoria && caminhoFormatado) {
            setCategoriaId(id);
            setSelectedCategory(categoria);
            setCaminho(caminhoFormatado);
            setValue('nome', '', {
                shouldDirty: true,
                shouldValidate: true,
            });

            const novosAtributos: Record<string, string> = {};
            categoria.atributos?.forEach((atributo: Atributo) => {
                novosAtributos[atributo.nome] = '';
            });
            setValue('atributos', novosAtributos);
        }
    };

    const isLoading = isSavingProduto || isAtualizandoCategoria;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Editar Informações Básicas</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} py={1}>
                    <Grid size={{ xs: 12 }}>
                        <Box>
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ mb: 2 }}>
                                    <SelectCategoriaProduto
                                        value={categoriaId}
                                        defaultValue={caminho}
                                        label="Categoria"
                                        onChange={handleCategoriaChange}
                                        required
                                    />
                                </Box>
                                <Box>
                                    {selectedCategory?.atributos && selectedCategory.atributos.length > 0 ? (
                                        <Typography variant="h6" gutterBottom mb={2}>
                                            Atributos da Categoria
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhum atributo encontrado para esta categoria
                                        </Typography>
                                    )}

                                    <Grid container spacing={2}>
                                        {selectedCategory?.atributos?.map((atributo: Atributo) => (
                                            <Grid size={{ xs: 12, md: 6 }} key={atributo.id}>
                                                <Controller
                                                    name={`atributos.${atributo.nome}`}
                                                    control={control}
                                                    rules={{
                                                        required: atributo.obrigatorio ? `${atributo.nome} é obrigatório` : false
                                                    }}
                                                    render={({ field, fieldState }) => {
                                                        const isMoeda = atributo.tipo === "moeda";
                                                        const isData = atributo.tipo === "data";

                                                        return (
                                                            <TextField
                                                                {...field}
                                                                value={field.value || ''}
                                                                label={atributo.nome}
                                                                fullWidth
                                                                required={atributo.obrigatorio}
                                                                error={!!fieldState.error}
                                                                {...(atributo.compoe_nome ? {
                                                                    helperText: 'Este atributo compoe o nome do produto'
                                                                } : { helperText: fieldState.error?.message })}
                                                                type={isData ? "date" : "text"}
                                                                InputLabelProps={
                                                                    isData
                                                                        ? { shrink: true, required: atributo.obrigatorio }
                                                                        : { required: atributo.obrigatorio }
                                                                }
                                                                inputProps={
                                                                    isMoeda
                                                                        ? { inputMode: "numeric" }
                                                                        : undefined
                                                                }
                                                                onChange={(e) => {
                                                                    if (isMoeda) {
                                                                        field.onChange(formatCurrencyCategory(e.target.value));
                                                                    } else {
                                                                        field.onChange(e.target.value);
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="nome"
                            control={control}
                            rules={{ required: "Obrigatório" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Nome do produto"
                                    fullWidth
                                    helperText="Edite o nome conforme necessário"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="codigo_de_barra"
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (value) {
                                        const stringValue = String(value).padStart(13, "0");
                                        return validateEAN13(stringValue) || "Código de barra EAN inválido";
                                    }
                                }
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Código de barra"
                                    fullWidth
                                    error={!!errors.codigo_de_barra}
                                    helperText={errors.codigo_de_barra?.message}
                                    slotProps={{ htmlInput: { maxLength: 13 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Controller
                            name="descricao_complementar"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Descrição Complementar"
                                    fullWidth
                                    error={!!errors.descricao_complementar}
                                    helperText={errors.descricao_complementar?.message}
                                    slotProps={{ htmlInput: { maxLength: 40 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="departamento"
                            control={control}
                            rules={{ required: "Departamento é obrigatório" }}
                            render={({ field }) => (
                                <SelectDepartamentoEstoqueMultiple
                                    value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                    onChange={val => {
                                        const newVal = Array.isArray(val) ? val[0] : val;
                                        field.onChange(newVal ? { id: newVal.id, nome: newVal.nome } : null);
                                    }}
                                    multiple={false}
                                    required
                                    error={!!errors.departamento}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="setor"
                            control={control}
                            rules={{ required: "Setor é obrigatório" }}
                            render={({ field }) => (
                                <SelectSetor
                                    value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                    onChange={val => {
                                        const newVal = Array.isArray(val) ? val[0] : val;
                                        field.onChange(newVal ? { id: newVal.id, nome: newVal.nome } : null);
                                    }}
                                    required
                                    error={!!errors.setor}
                                    multiple={false}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    color="primary"
                    variant="contained"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}